import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

/**
 * Change-password screen inside the admin/staff ERP.
 * Forced flow for newly-issued staff logins + optional voluntary rotation.
 */
export default function AdminChangePassword() {
  const { user, changePassword, logout } = useAuth();
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const forced = !!user?.must_change_password;

  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (newPw !== confirmPw) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await changePassword(currentPw, newPw);
      toast.success("Password updated");
      nav("/admin");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl" data-testid="admin-change-password-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 border border-brand-blue/50 flex items-center justify-center brand-blue">
          <KeyRound size={18} />
        </div>
        <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange">
          // {forced ? "First-time login" : "Account security"}
        </div>
      </div>
      <h1 className="font-display text-4xl md:text-5xl font-black uppercase leading-none tracking-tighter">
        {forced ? <>Set a new<br /><span className="brand-blue">private password</span>.</> : <>Rotate your<br /><span className="brand-blue">password</span>.</>}
      </h1>
      <p className="mt-4 text-sm text-white/60">
        Signed in as <span className="brand-blue">{user?.email}</span>
        {user?.role === "staff" && <> · Role: <span className="brand-orange uppercase font-mono text-xs">staff</span></>}
      </p>

      <form onSubmit={submit} className="mt-8 space-y-4">
        <input
          type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)}
          placeholder={forced ? "Temporary password issued by admin" : "Current password"} required
          data-testid="admin-cp-current"
          className="w-full bg-transparent border border-white/20 py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-brand-blue"
        />
        <input
          type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
          placeholder="New password (min 6 chars)" required minLength={6}
          data-testid="admin-cp-new"
          className="w-full bg-transparent border border-white/20 py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-brand-blue"
        />
        <input
          type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
          placeholder="Confirm new password" required minLength={6}
          data-testid="admin-cp-confirm"
          className="w-full bg-transparent border border-white/20 py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-brand-blue"
        />
        <button
          type="submit" disabled={loading}
          data-testid="admin-cp-submit"
          className="w-full bg-brand-blue text-white py-4 font-bold text-sm uppercase tracking-widest hover:glow-blue disabled:opacity-50"
        >
          {loading ? "…" : "Update Password"}
        </button>
        {forced && (
          <button
            type="button"
            onClick={async () => { await logout(); nav("/management/login"); }}
            className="w-full text-xs font-mono uppercase tracking-widest text-white/50 hover:text-white pt-2"
            data-testid="admin-cp-logout"
          >
            Cancel & Logout
          </button>
        )}
      </form>
    </div>
  );
}
